import { apiGet, apiPost } from '../lib/api';

// Booking-related convenience functions used by the Booking Wizard UI
const bookingService = {
  async fetchServiceById(serviceId) {
    const res = await apiGet(`/services/${serviceId}`);
    return res?.data || res;
  },

  async fetchCreativeById(creativeId) {
    const res = await apiGet(`/creatives/${creativeId}`);
    return res?.data || res?.data?.data || res;
  },

  async listAvailabilities(creativeId, opts = {}) {
    const params = { creative_id: creativeId };
    if (opts.starts_before) params.starts_before = opts.starts_before;
    if (opts.ends_after) params.ends_after = opts.ends_after;
    const res = await apiGet('/availabilities', params);
    const data = res?.data || res;
    return data?.data?.availabilities || data?.availabilities || [];
  },

  // Get service packages
  async getServicePackages(serviceId) {
    const res = await apiGet(`/services/${serviceId}/packages`);
    return res?.data?.packages || res?.packages || [];
  },

  // Basic price calculator: prefer package_price when present; else hourly_rate; else base_price; else price
  calculatePrice(service, { hours = 1, isPackage = false }) {
    const currency = String(service?.currency || 'SEK').toUpperCase();
    const pkgPrice = service?.package_price ?? null;
    const hourly = service?.hourly_rate ?? null;
    const base = service?.base_price ?? null;
    const fallback = service?.price ?? null;
    let amount = 0;
    if (isPackage && pkgPrice != null) amount = Number(pkgPrice);
    else if (hourly != null) amount = Number(hourly) * Math.max(Number(hours) || 1, 1);
    else if (base != null) amount = Number(base);
    else if (fallback != null) amount = Number(fallback);
    return { amount: Math.round(amount), currency };
  },

  async createBooking({ client_id, service, scheduled_at, total_price, currency, notes, meeting_type }) {
    const totalAmount = typeof total_price === 'number' ? total_price : Number(total_price || 0);
    const payload = {
      client_id,
      service_id: service?.id,
      creative_id: service?.creative_id, // Use creative_id from service
      scheduled_at: scheduled_at || undefined,
      total_price: totalAmount,
      creative_amount: totalAmount,
      currency: currency || service?.currency || 'SEK',
      notes: notes || undefined,
      meeting_type: meeting_type || undefined,
    };
    const res = await apiPost('/bookings', payload);
    return res?.data || res;
  },

  // Charge a booking using the existing payments endpoint
  async chargeBooking({ booking_id, payer_id, amount, currency = 'SEK', provider = 'stripe' }) {
    const res = await apiPost('/payments/charge', {
      booking_id,
      payer_id,
      amount,
      currency,
      provider,
    });
    return res?.data || res;
  },

  // Quick Pay - One-click booking + payment initiation
  // Creates booking and returns Stripe checkout URL in one call
  async quickPay({
    creative_id,
    service_id,
    package_id,
    meeting_type = 'hourly', // hourly, package, fika_online, fika_in_person
    scheduled_at,
    hours = 1,
    notes,
  }) {
    const res = await apiPost('/payments/quick-pay', {
      creative_id,
      service_id,
      package_id,
      meeting_type,
      scheduled_at,
      hours,
      notes,
    });
    return res?.data || res;
  },

  // Get booking by ID
  async getBookingById(bookingId) {
    const res = await apiGet(`/bookings/${bookingId}`);
    return res?.data || res;
  },

  // Get my bookings (as client or creative)
  async getMyBookings(params = {}) {
    const res = await apiGet('/bookings', params);
    return res?.data?.bookings || res?.bookings || [];
  },

  // Update booking status
  async updateBookingStatus(bookingId, status, reason) {
    const res = await apiPost(`/bookings/${bookingId}`, {
      status,
      status_reason: reason,
    }, { method: 'PATCH' });
    return res?.data || res;
  },

  // ==================== ESCROW FUNCTIONS ====================

  // Get escrow status for a booking
  async getEscrowStatus(bookingId) {
    const res = await apiGet(`/payments/escrow/${bookingId}`);
    return res?.data || res;
  },

  // Release escrow to creative (after successful meeting)
  async releaseEscrow(bookingId) {
    const res = await apiPost('/payments/escrow/release', {
      booking_id: bookingId,
    });
    return res?.data || res;
  },

  // Raise dispute on escrow
  async disputeEscrow(bookingId, reason) {
    const res = await apiPost('/payments/escrow/dispute', {
      booking_id: bookingId,
      reason,
    });
    return res?.data || res;
  },
};

export default bookingService;
